import React, { useState, useEffect, useRef } from 'react';
import { useAlert, useConfirm } from '../components/ConfirmModal';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/shared/DashboardLayout';
import { useTheme } from '../context/ThemeContext';
import VoiceRecorder from '../components/VoiceRecorder';
import ReferralCard from '../components/ReferralCard';
import { useAuth } from '../context/AuthContext';
import useSettings from '../utils/useSettings';
import API, { setAuthToken } from '../utils/api';
import { isNative, isIOS } from '../utils/capacitor';
import { entitledPlan } from '../utils/entitlements';
import { trackProfileUpdated, trackBillingPortalOpened, trackReminderSaved, trackPushNotificationEnabled, trackLogout } from '../utils/analytics';
import { openCheckoutUrl } from '../utils/twa';
import { shouldUseIap, manageSubscriptionsIap, restoreIap } from '../utils/iap';
import { enablePush, disablePush, initPushListeners, pushSupported as isPushSupported, getDeviceStatus } from '../utils/push';
import { useTour } from '../components/ProductTour';
import safeStorage from '../utils/safeStorage';

const TIMEZONES = [
  'America/New_York','America/Chicago','America/Denver','America/Los_Angeles',
  'America/Phoenix','America/Anchorage','Pacific/Honolulu',
  'Europe/London','Europe/Paris','Europe/Berlin','Europe/Madrid','Europe/Rome',
  'Europe/Amsterdam','Europe/Stockholm','Europe/Helsinki','Europe/Moscow',
  'Asia/Dubai','Asia/Kolkata','Asia/Dhaka','Asia/Bangkok','Asia/Singapore',
  'Asia/Tokyo','Asia/Seoul','Asia/Shanghai','Asia/Hong_Kong',
  'Australia/Sydney','Australia/Melbourne','Australia/Brisbane','Australia/Perth',
  'Pacific/Auckland','America/Sao_Paulo','America/Buenos_Aires',
  'America/Toronto','America/Vancouver','America/Mexico_City',
];

const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map(c => c.charCodeAt(0)));
};

const SettingsPage = () => {
  const { darkMode: dm } = useTheme();
  const alert   = useAlert();
  const confirm = useConfirm();
  const { user, updateUser, logout, refreshUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [profile, setProfile] = useState({ name: user?.name || '', avatar: user?.avatar || '' });
  const [profileMsg, setProfileMsg] = useState('');
  const [saving, setSaving] = useState(false);

  // Digest state
  const [digest, setDigest] = useState(null);
  const [digestSaving, setDigestSaving] = useState(false);
  const [digestMsg, setDigestMsg] = useState('');
  const [digestTestSending, setDigestTestSending] = useState(false);
  const [digestTestResult, setDigestTestResult] = useState('');

  // Marketing opt-in state
  const [marketingOptIn, setMarketingOptIn] = useState(user?.marketingOptIn !== false);
  const [marketingSaving, setMarketingSaving] = useState(false);
  const [marketingMsg, setMarketingMsg] = useState('');

  const [signOutLoading, setSignOutLoading] = useState(false);
  const [signOutMsg, setSignOutMsg] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);

  // GDPR Art. 17. The button previously just told the user to email support,
  // even though the API and the Guide/FAQ/Privacy pages all describe this flow.
  const handleDeleteAccount = async () => {
    // Deleting the account cancels a subscription paid on our website — the
    // server does that. An App Store / Google Play subscription is billed by
    // the store, and nothing on our side can stop it: the dialog promised it
    // would be cancelled and the store went on charging for a deleted account.
    // Apple requires apps to say so, and how to cancel.
    const sub = user?.subscription || {};
    const storeBilled = ['app_store', 'play_store'].includes(sub.store)
      && ['active', 'trialing'].includes(sub.status)
      && sub.plan && sub.plan !== 'free'
      && !sub.cancelAtPeriodEnd;
    if (storeBilled) {
      const apple      = sub.store === 'app_store';
      const manageHere = shouldUseIap() && apple === isIOS();
      const go = await confirm({
        title:   `Cancel your ${apple ? 'App Store' : 'Google Play'} subscription first`,
        message: `Your subscription is billed by ${apple ? 'Apple' : 'Google'}, so deleting your account does NOT cancel it — you would keep being charged. `
          + (apple
            ? 'On your iPhone or iPad: Settings → [your name] → Subscriptions → Dreamy Bedtime → Cancel Subscription.'
            : 'In the Play Store app: tap your profile → Payments & subscriptions → Subscriptions → Dreamy Bedtime → Cancel.'),
        confirmLabel: manageHere ? 'Open subscriptions' : "I've cancelled it",
        variant: 'warning',
      });
      if (!go) return;
      if (manageHere) { manageSubscriptionsIap(); return; }
    }

    const ok = await confirm({
      title: 'Permanently delete your account?',
      message: 'This erases your children\'s profiles, every story, all share links and your voice recording, and cancels any subscription you pay for on our website. It cannot be undone.',
      confirmLabel: 'Continue',
      variant: 'danger',
    });
    if (!ok) return;

    // Typing the email is the confirmation the API requires
    const typed = window.prompt(`To confirm, type your email address:\n${user?.email || ''}`);
    if (!typed) return;
    if (typed.trim().toLowerCase() !== String(user?.email || '').toLowerCase()) {
      await alert({ title: 'Email did not match', message: 'Your account has NOT been deleted.', danger: true });
      return;
    }

    setDeletingAccount(true);
    try {
      await API.delete('/auth/account', { data: { confirmEmail: typed.trim() } });
      await alert({
        title: 'Account deleted',
        message: 'Your account and all associated data have been permanently deleted. Thank you for trying Dreamy Bedtime.',
      });
      logout();
      navigate('/');
    } catch (err) {
      await alert({
        title: 'Could not delete your account',
        message: err.response?.data?.message || 'Please try again, or contact support@dreamybedtime.com.',
        danger: true,
      });
    } finally { setDeletingAccount(false); }
  };

  // Reminder state
  const [reminder, setReminder] = useState(null);
  const [reminderSaving, setReminderSaving] = useState(false);
  const [reminderMsg, setReminderMsg] = useState('');
  const [testSending, setTestSending] = useState(false);
  const [testResult, setTestResult] = useState('');
  const [pushSupported, setPushSupported] = useState(false);
  const [pushPermission, setPushPermission] = useState('default');
  const [disablingPush, setDisablingPush] = useState(false);
  // Per-device push state. reminder.pushEnabled is account-wide, so using it
  // here told a second device it was already active and offered it a Disable
  // button with no token behind it.
  const [pushDevice, setPushDevice] = useState({ registered: false, otherDevices: 0, loading: true });

  const refreshPushDevice = React.useCallback(async () => {
    try {
      const st = await getDeviceStatus();
      setPushDevice({ registered: st.registered, otherDevices: st.otherDevices, loading: false });
      setPushPermission(st.permission);
    } catch (_) {
      setPushDevice(d => ({ ...d, loading: false }));
    }
  }, []);
  const fcmListenersRef = useRef(null); // track Capacitor PushNotifications listeners

  // Story of the Night state
  const [son, setSon] = useState(null);
  const [sonSaving, setSonSaving] = useState(false);
  const [sonMsg, setSonMsg] = useState('');
  const [sonTestSending, setSonTestSending] = useState(false);
  const [sonTestResult, setSonTestResult] = useState('');

  // Family members state
  const [familyData,      setFamilyData]      = useState(null);
  const [familyLoading,   setFamilyLoading]   = useState(false);
  const [inviteEmail,     setInviteEmail]     = useState('');
  const [inviteRole,      setInviteRole]      = useState('read_only');
  const [inviteSending,   setInviteSending]   = useState(false);
  const [familyMsg,       setFamilyMsg]       = useState({ text: '', type: '' }); // type: 'success' | 'error'
  const [leavingFamily,   setLeavingFamily]   = useState(false);

  // Trialing family users get full Family access during their free trial.
  // Deliberately the user's OWN subscription, not isFamilyEntitled(user). This
  // gates family MANAGEMENT — recording or deleting the voiceprint, inviting and
  // removing co-parents — all of which the API restricts to the primary parent.
  // A co-parent must stay false here: switching this to the shared entitlement
  // would show them a VoiceRecorder whose upload 403s and whose delete 404s.
  // Use isFamilyEntitled(user) for gating family FEATURES a co-parent may use.
  const isFamilyActive = user?.subscription?.plan === 'family' && ['active', 'trialing'].includes(user?.subscription?.status);

  useEffect(() => {
    // Firebase Cloud Messaging covers web, iOS and Android through one path.
    setPushSupported(isPushSupported());
    if (!isNative() && 'Notification' in window) setPushPermission(Notification.permission);
    // Ask the server whether THIS device's token is registered. Never prompts.
    refreshPushDevice();

    API.get('/reminders/settings').then(res => setReminder(res.data.reminder)).catch(() => {});
    API.get('/digest/settings').then(res => setDigest(res.data.digest)).catch(() => {});
    API.get('/reminders/story-of-night').then(res => setSon({ ...res.data.storyOfNight, pushAvailable: res.data.pushAvailable })).catch(() => {});

    // Notification tap → deep link, foreground banner, and token-refresh sync.
    const cleanup = initPushListeners({
      onForeground: (n) => {
        if (!n) return;
        setReminderMsg(`🔔 ${n.title || 'Dreamy Bedtime'}: ${n.body || ''}`);
        setTimeout(() => setReminderMsg(''), 6000);
      },
    });
    return cleanup;
  }, []);

  // Deep-link support: /dashboard/settings#referral scrolls to the referral card
  // (used by the "Invite a friend" nudge on the story completion celebration).
  useEffect(() => {
    if (location.hash !== '#referral') return undefined;
    const t = setTimeout(() => {
      document.getElementById('referral')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 400);
    return () => clearTimeout(t);
  }, [location.hash]);

  useEffect(() => {
    if (!isFamilyActive && !user?.coParentOf) return;
    setFamilyLoading(true);
    API.get('/family/members')
      .then(res => setFamilyData(res.data))
      .catch(() => {})
      .finally(() => setFamilyLoading(false));
  }, [isFamilyActive, user?.coParentOf]);

  // The user's own block list already arrives on the user object, so unblocking
  // reuses the existing per-id endpoint — no new API surface, and no need to
  // store or expose who the blocked authors are.
  const blockedCount = (user?.blockedUsers || []).length;
  const [unblocking, setUnblocking] = useState(false);
  const handleUnblockAll = async () => {
    const ok = await confirm({
      title: 'Unblock everyone?',
      message: `Stories from ${blockedCount === 1 ? 'this storyteller' : 'these ' + blockedCount + ' storytellers'} will appear in your community feed again.`,
      confirmLabel: 'Unblock all',
      variant: 'warning',
    });
    if (!ok) return;
    setUnblocking(true);
    try {
      const ids = user?.blockedUsers || [];
      await Promise.all(ids.map(id => API.delete(`/stories/community/blocks/${id}`).catch(() => null)));
      await refreshUser();
    } finally {
      setUnblocking(false);
    }
  };

  const handleProfileSave = async (e) => {
    e.preventDefault(); setSaving('profile'); setProfileMsg('');
    try {
      const res = await API.put('/auth/profile', profile);
      updateUser(res.data.user);
      trackProfileUpdated();
      setProfileMsg('Profile updated successfully!');
    } catch (err) { setProfileMsg('Failed to update profile.'); }
    finally { setSaving(false); }
  };

  const handlePortal = async () => {
    // In the native app, IAP subscriptions are managed in the App Store / Play
    // Store — but only the one THIS device's store holds. A plan paid on the
    // website, or through the other store, is not there: the button opened an
    // empty subscriptions page and the customer could find no way to cancel.
    if (shouldUseIap()) {
      const sub  = user?.subscription || {};
      const here = isIOS() ? 'app_store' : 'play_store';
      if (sub.stripeSubscriptionId && !['app_store', 'play_store'].includes(sub.store)) {
        await alert({
          title:   'Managed on our website',
          message: `Your subscription is billed through our website, not the ${isIOS() ? 'App Store' : 'Play Store'}, so it is managed there: sign in on a computer or phone browser, then Settings → Manage Subscription.`,
        });
        return;
      }
      if (['app_store', 'play_store'].includes(sub.store) && sub.store !== here) {
        await alert(sub.store === 'app_store'
          ? { title: 'Managed by Apple', message: 'Your subscription is billed through the App Store: on an iPhone or iPad signed in to the same Apple ID, open Settings → [your name] → Subscriptions.' }
          : { title: 'Managed by Google Play', message: 'Your subscription is billed through Google Play: on an Android device signed in to the same Google account, open the Play Store → Payments & subscriptions → Subscriptions.' });
        return;
      }
      manageSubscriptionsIap();
      return;
    }
    // On the web, a subscription bought via in-app purchase has no Stripe billing
    // to manage — point them to the right store instead of an empty portal.
    const store = user?.subscription?.store;
    if (store === 'app_store' || store === 'play_store') {
      await alert({
        title: 'Manage in the app store',
        message: store === 'app_store'
          ? 'You subscribed through Apple, so this plan is managed on your Apple device: Settings → [your name] → Subscriptions (or apps.apple.com/account/subscriptions).'
          : 'You subscribed through Google Play, so this plan is managed there: open the Play Store app → Payments & subscriptions → Subscriptions (or play.google.com/store/account/subscriptions).',
      });
      return;
    }
    try {
      trackBillingPortalOpened();
      const res = await API.post('/subscriptions/portal');
      openCheckoutUrl(res.data.url);
    } catch (err) { await alert({ title: 'Unable to open billing portal', message: 'Please contact support@dreamybedtime.com for help.', danger: true }); }
  };

  // Apple requires an explicit "restore purchases" path for IAP.
  const handleRestore = async () => {
    try {
      const r = await restoreIap();
      // Restoring on the device changes nothing on our server by itself: no
      // webhook fires when purchases are restored to the account that already
      // owns them. So "Purchases restored" appeared while the account stayed on
      // Free. Ask the server to read the entitlement from RevenueCat.
      try { await API.post('/subscriptions/iap/sync'); } catch (_) {}
      await refreshUser();
      const storeName = isIOS() ? 'App Store' : 'Google Play';
      await alert({
        title:   r.hasActive ? 'Purchases restored' : 'Nothing to restore',
        message: r.hasActive
          ? 'Your subscription has been restored on this device.'
          // A plan paid on the website or through the other store is already on
          // the account, on every device — "nothing found" read as if it had
          // been lost.
          : entitledPlan(user) !== 'free'
            ? `There are no ${storeName} purchases to restore — your plan is already active on this account, on every device you sign in to.`
            : `No ${storeName} purchases were found for this ${isIOS() ? 'Apple ID' : 'Google account'}.`,
      });
    } catch (err) {
      await alert({ title: 'Restore failed', message: err?.message || 'Could not restore purchases.', danger: true });
    }
  };

  const saveDigest = async (patch) => {
    const updated = { ...digest, ...patch };
    setDigest(updated);
    setDigestSaving(true); setDigestMsg('');
    try {
      const res = await API.put('/digest/settings', patch);
      setDigest(res.data.digest);
      setDigestMsg('Saved!');
      setTimeout(() => setDigestMsg(''), 2000);
    } catch { setDigestMsg('Failed to save.'); }
    finally { setDigestSaving(false); }
  };

  const handleSendTestDigest = async () => {
    setDigestTestSending(true); setDigestTestResult('');
    try {
      await API.post('/digest/test');
      setDigestTestResult('Digest sent to your email!');
    } catch (err) {
      setDigestTestResult(err.response?.data?.message || 'Test failed.');
    } finally { setDigestTestSending(false); }
  };

  const handleMarketingToggle = async (optIn) => {
    setMarketingOptIn(optIn);
    setMarketingSaving(true); setMarketingMsg('');
    try {
      await API.put('/auth/marketing', { optIn });
      setMarketingMsg(optIn ? 'Subscribed to marketing emails.' : 'Unsubscribed from marketing emails.');
      setTimeout(() => setMarketingMsg(''), 3000);
    } catch {
      setMarketingOptIn(!optIn); // revert on failure
      setMarketingMsg('Failed to update preference.');
    } finally { setMarketingSaving(false); }
  };

  const handleSignOutOtherDevices = async () => {
    setSignOutLoading(true); setSignOutMsg('');
    try {
      const res = await API.put('/auth/sign-out-other-devices');
      // Replace the in-memory token so this device stays logged in with the new sessionId.
      setAuthToken(res.data.token);
      safeStorage.setItem('dreamybedtime_token', res.data.token);
      setSignOutMsg('All other devices have been signed out.');
      setTimeout(() => setSignOutMsg(''), 4000);
    } catch {
      setSignOutMsg('Failed to sign out other devices. Please try again.');
    } finally { setSignOutLoading(false); }
  };

  const saveReminder = async (patch) => {
    const updated = { ...reminder, ...patch };
    setReminder(updated);
    setReminderSaving(true); setReminderMsg('');
    try {
      const res = await API.put('/reminders/settings', patch);
      setReminder(res.data.reminder);
      setReminderMsg('Saved!');
      if (patch.enabled !== undefined) trackReminderSaved(patch.enabled);
      setTimeout(() => setReminderMsg(''), 2000);
    } catch { setReminderMsg('Failed to save.'); }
    finally { setReminderSaving(false); }
  };

  const handleEnablePush = async () => {
    // Unified Firebase Cloud Messaging path (web + iOS + Android).
    if (!isPushSupported()) {
      await saveReminder({ emailEnabled: true });
      setReminderMsg('Push isn’t available here — email reminders enabled instead.');
      setTimeout(() => setReminderMsg(''), 4000);
      return;
    }
    try {
      const r = await enablePush();
      if (!r.granted) {
        await saveReminder({ emailEnabled: true });
        setReminderMsg('Permission denied — email reminders enabled instead.');
        setTimeout(() => setReminderMsg(''), 4000);
        return;
      }
      if (!r.token) {
        setReminderMsg('Could not get a push token. Please try again.');
        setTimeout(() => setReminderMsg(''), 4000);
        return;
      }
      setPushPermission('granted');
      setReminder(rm => ({ ...rm, pushEnabled: true, pushDeviceCount: r.deviceCount }));
      await refreshPushDevice();
      trackPushNotificationEnabled();
      setReminderMsg(`Push notifications enabled! (${r.deviceCount} device${r.deviceCount > 1 ? 's' : ''} active)`);
      setTimeout(() => setReminderMsg(''), 3000);
    } catch (err) {
      setReminderMsg('Could not enable push: ' + (err.message || 'Unknown error'));
      setTimeout(() => setReminderMsg(''), 5000);
    }
  };

  const handleDisablePush = async () => {
    setDisablingPush(true);
    try {
      const r = await disablePush();
      setReminder(rm => ({ ...rm, pushEnabled: r.deviceCount > 0, pushDeviceCount: r.deviceCount }));
      await refreshPushDevice();
      setReminderMsg(r.deviceCount > 0
        ? `Push disabled on this device. ${r.deviceCount} other device${r.deviceCount > 1 ? 's' : ''} still active.`
        : 'Push notifications disabled on all devices.');
      setTimeout(() => setReminderMsg(''), 3000);
    } catch (err) {
      // Show WHY. The old handler swallowed every error into "Failed to disable
      // push", which is what a silent server-side no-op looked like too — so the
      // one visible symptom was the switch flipping back with no explanation.
      setReminderMsg(err?.message
        || err?.response?.data?.message
        || 'Could not disable push on this device. Please try again.');
      setTimeout(() => setReminderMsg(''), 6000);
      // Re-read the truth: if the token was already gone the button should flip
      // to Enable rather than keep offering a Disable that cannot work.
      await refreshPushDevice();
    } finally {
      setDisablingPush(false);
    }
  };

  const handleSendTest = async () => {
    setTestSending(true); setTestResult('');
    try {
      const res = await API.post('/reminders/test');
      const { email, push } = res.data.results;
      const parts = [];
      if (email === 'sent') parts.push('Email sent');
      if (push  === 'sent') parts.push('Push sent');
      // A failed channel returns its error message rather than 'sent'; saying
      // only what succeeded hid the reason a test appeared to do nothing.
      if (email && email !== 'sent') parts.push(`Email failed: ${email}`);
      if (push  && push  !== 'sent') parts.push(`Push failed: ${push}`);
      if (!parts.length) parts.push('No channels enabled — turn on email or push above');
      // Android draws a tray notification only when the app is in the
      // background, so a test fired from this screen can deliver successfully
      // and still show nothing. Say so rather than let it look broken.
      if (push === 'sent' && isNative()) {
        parts.push('minimise the app to see it in your notification tray');
      }
      setTestResult(parts.join(' · '));
    } catch (err) {
      setTestResult(err.response?.data?.message || 'Test failed.');
    } finally { setTestSending(false); }
  };

  const saveSon = async (patch) => {
    setSon(prev => ({ ...prev, ...patch }));
    setSonSaving(true); setSonMsg('');
    try {
      const res = await API.put('/reminders/story-of-night', patch);
      setSon(prev => ({ ...prev, ...res.data.storyOfNight }));
      setSonMsg('Saved!');
      setTimeout(() => setSonMsg(''), 2000);
    } catch { setSonMsg('Failed to save.'); }
    finally { setSonSaving(false); }
  };

  const handleSendTestSon = async () => {
    setSonTestSending(true); setSonTestResult('');
    try {
      const res = await API.post('/reminders/story-of-night/test');
      // The endpoint now 502s when nothing was delivered, so this only claims
      // success when a device actually accepted it.
      setSonTestResult(res.data?.sent
        ? `Test notification sent to ${res.data.sent} device${res.data.sent > 1 ? 's' : ''}!`
        : 'Test notification sent!');
    } catch (err) {
      setSonTestResult(err.response?.data?.message || 'Test failed.');
    } finally { setSonTestSending(false); }
  };

  const handleSendInvite = async (e) => {
    e.preventDefault();
    setInviteSending(true); setFamilyMsg({ text: '', type: '' });
    try {
      await API.post('/family/invite', { email: inviteEmail, role: inviteRole });
      setFamilyMsg({ text: `Invite sent to ${inviteEmail}!`, type: 'success' });
      setInviteEmail('');
      const res = await API.get('/family/members');
      setFamilyData(res.data);
    } catch (err) {
      setFamilyMsg({ text: err.response?.data?.message || 'Failed to send invite.', type: 'error' });
    } finally { setInviteSending(false); }
  };

  const handleRevokeInvite = async (inviteId) => {
    const ok = await confirm({
      title: 'Revoke invitation?',
      message: 'The invite link will stop working immediately. You can re-invite this person later.',
      confirmLabel: 'Revoke',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await API.delete(`/family/invites/${inviteId}`);
      setFamilyData(d => ({ ...d, pendingInvites: d.pendingInvites.filter(i => i._id !== inviteId) }));
    } catch (err) {
      setFamilyMsg({ text: err.response?.data?.message || 'Failed to revoke invite.', type: 'error' });
    }
  };

  const handleRemoveMember = async (memberId) => {
    const member = familyData?.members?.find(m => m._id === memberId);
    const ok = await confirm({
      title: 'Remove family member?',
      message: `${member?.name || 'This person'} will lose access to your children and story library. You can re-invite them later.`,
      confirmLabel: 'Remove',
      danger: true,
    });
    if (!ok) return;
    try {
      await API.delete(`/family/members/${memberId}`);
      setFamilyData(d => ({ ...d, members: d.members.filter(m => m._id !== memberId) }));
    } catch (err) {
      setFamilyMsg({ text: err.response?.data?.message || 'Failed to remove member.', type: 'error' });
    }
  };

  const handleToggleRole = async (memberId, newRole) => {
    try {
      await API.put(`/family/members/${memberId}/role`, { role: newRole });
      setFamilyData(d => ({
        ...d,
        members: d.members.map(m => m._id === memberId ? { ...m, coParentRole: newRole } : m),
      }));
    } catch (err) {
      setFamilyMsg({ text: err.response?.data?.message || 'Failed to update role.', type: 'error' });
    }
  };

  const handleLeaveFamily = async () => {
    const primary = familyData?.primaryParent;
    const ok = await confirm({
      title: 'Leave family?',
      message: `You'll lose access to ${primary?.name || 'the primary parent'}'s children and stories. Your own data will become visible again on your account.`,
      confirmLabel: 'Leave Family',
      danger: true,
    });
    if (!ok) return;
    setLeavingFamily(true);
    try {
      await API.delete('/family/leave');
      await refreshUser();
    } catch (err) {
      setFamilyMsg({ text: err.response?.data?.message || 'Failed to leave family.', type: 'error' });
    } finally { setLeavingFamily(false); }
  };

  const planColors = { free:'secondary', basic:'purple', family:'warning' };
  const planColor = planColors[user?.subscription?.plan] || 'secondary';
  const { settings } = useSettings();
  const flags = settings.featureFlags || {};
  const { replayTour } = useTour();

  return (
    <DashboardLayout title="Settings">
      <div className="row g-4">
        <div className="col-lg-8">

          {/* Profile */}
          <div className="card border-0 shadow-sm mb-4" style={{borderRadius:'16px'}}>
            <div className="card-body p-4">
              <h5 className="fw-bold mb-4" style={{color:'var(--text-main)'}}>
                <i className="bi bi-person-circle me-2"></i>Profile
              </h5>
              {profileMsg && <div className={`alert py-2 small ${profileMsg.includes('success') ? 'alert-success' : 'alert-danger'}`}>{profileMsg}</div>}
              <form onSubmit={handleProfileSave}>
                <div className="row g-3 mb-3">
                  <div className="col-md-6">
                    <label className="form-label fw-semibold small">Full Name</label>
                    <input className="form-control" value={profile.name}
                      onChange={e => setProfile(p => ({...p, name:e.target.value}))} required />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold small">Email</label>
                    <input className="form-control" value={user?.email} disabled />
                  </div>
                </div>
                <button type="submit" className="btn btn-dream rounded-pill px-4" disabled={saving === 'profile'}>
                  {saving === 'profile' ? <><span className="spinner-border spinner-border-sm me-2"></span>Saving...</> : 'Save Profile'}
                </button>
              </form>
            </div>
          </div>

          {/* Bedtime Reminders */}
          {flags.bedtimeReminder !== false && <div className="card border-0 shadow-sm mb-4" style={{borderRadius:'16px'}}>
            <div className="card-body p-4">
              <div className="d-flex align-items-center justify-content-between mb-4">
                <h5 className="fw-bold mb-0" style={{color:'var(--text-main)'}}>
                  <i className="bi bi-bell-fill me-2" style={{color:'#6f42c1'}}></i>Bedtime Reminders
                </h5>
                {reminder && (
                  <div className="form-check form-switch mb-0">
                    <input className="form-check-input" type="checkbox" role="switch"
                      style={{cursor:'pointer', width:'48px', height:'24px'}}
                      checked={!!reminder.enabled}
                      onChange={e => saveReminder({ enabled: e.target.checked })} />
                  </div>
                )}
              </div>

              {!reminder ? (
                <div className="text-center py-3"><span className="spinner-border spinner-border-sm" style={{color:'#6f42c1'}}></span></div>
              ) : (
                <>
                  {reminderMsg && (
                    <div className={`alert py-2 small mb-3 ${reminderMsg.includes('ailed') || reminderMsg.includes('denied') ? 'alert-danger' : 'alert-success'}`}>
                      {reminderMsg}
                    </div>
                  )}

                  {!reminder.enabled && (
                    <p className="text-muted small mb-3">Enable reminders to get a nightly nudge to create tonight's story.</p>
                  )}

                  <div className={reminder.enabled ? '' : 'opacity-50 pe-none'}>
                    {/* Time + Timezone */}
                    <div className="row g-3 mb-4">
                      <div className="col-sm-5">
                        <label className="form-label fw-semibold small">Reminder Time</label>
                        <input type="time" className="form-control"
                          value={reminder.time || '20:00'}
                          onChange={e => saveReminder({ time: e.target.value })} />
                      </div>
                      <div className="col-sm-7">
                        <label className="form-label fw-semibold small">Timezone</label>
                        <select className="form-select" value={reminder.timezone || 'America/New_York'}
                          onChange={e => saveReminder({ timezone: e.target.value })}>
                          {TIMEZONES.map(tz => <option key={tz} value={tz}>{tz.replace(/_/g,' ')}</option>)}
                        </select>
                      </div>
                    </div>

                    {/* Email toggle */}
                    <div className="d-flex align-items-center justify-content-between p-3 rounded-3 mb-3"
                      style={{background:'var(--header-bg)'}}>
                      <div>
                        <div className="fw-semibold small" style={{color:'var(--text-main)'}}>
                          <i className="bi bi-envelope-fill me-2" style={{color:'#6f42c1'}}></i>Email Reminder
                        </div>
                        <div className="text-muted" style={{fontSize:'12px'}}>Send to {user?.email}</div>
                      </div>
                      <div className="form-check form-switch mb-0">
                        <input className="form-check-input" type="checkbox" role="switch"
                          style={{cursor:'pointer'}}
                          checked={!!reminder.emailEnabled}
                          onChange={e => saveReminder({ emailEnabled: e.target.checked })} />
                      </div>
                    </div>

                    {/* Push toggle — always shown; falls back to email when push unavailable */}
                    <div className="d-flex align-items-center justify-content-between p-3 rounded-3 mb-4"
                      style={{background:'var(--header-bg)'}}>
                      <div>
                        <div className="fw-semibold small" style={{color:'var(--text-main)'}}>
                          <i className="bi bi-phone-fill me-2" style={{color:'#6f42c1'}}></i>Push Notification
                        </div>
                        <div className="text-muted" style={{fontSize:'12px'}}>
                          {!pushSupported
                            ? 'Not available — tap Enable to use email reminders instead'
                            : pushPermission === 'denied'
                              ? 'Blocked — enable in browser settings'
                              : pushDevice.loading
                                ? 'Checking this device…'
                                : pushDevice.registered
                                  ? <>Active on this device{pushDevice.otherDevices > 0 && <span className="ms-1 badge rounded-pill" style={{background:'#e8d5ff',color:'#6f42c1',fontSize:'10px'}}>+{pushDevice.otherDevices} other</span>}</>
                                  : pushDevice.otherDevices > 0
                                    ? `Not active here — on ${pushDevice.otherDevices} other device${pushDevice.otherDevices > 1 ? 's' : ''}`
                                    : 'Get a reminder on this device'}
                        </div>
                      </div>
                      {(pushSupported ? pushPermission !== 'denied' : true) && !pushDevice.loading && (
                        pushDevice.registered
                          ? <button className="btn btn-sm btn-outline-secondary rounded-pill" onClick={handleDisablePush} disabled={disablingPush}>
                              {disablingPush
                                ? <><span className="spinner-border spinner-border-sm me-2"></span>Disabling…</>
                                : 'Disable this device'}
                            </button>
                          : <button className="btn btn-sm btn-dream rounded-pill" onClick={handleEnablePush}>Enable</button>
                      )}
                    </div>

                    {/* Test button */}
                    <div className="d-flex align-items-center gap-3">
                      <button className="btn btn-outline-secondary rounded-pill"
                        onClick={handleSendTest} disabled={testSending || reminderSaving}>
                        {testSending
                          ? <><span className="spinner-border spinner-border-sm me-2"></span>Sending...</>
                          : <><i className="bi bi-send me-2"></i>Send Test Reminder</>}
                      </button>
                      {testResult && <span className="small text-muted">{testResult}</span>}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>}

          {/* Story of the Night */}
          <div className="card border-0 shadow-sm mb-4" style={{borderRadius:'16px'}}>
            <div className="card-body p-4">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <div>
                  <h5 className="fw-bold mb-1" style={{color:'var(--text-main)'}}>
                    <i className="bi bi-moon-stars-fill me-2" style={{color:'#6f42c1'}}></i>Story of the Night
                  </h5>
                  <p className="text-muted small mb-0">
                    Get a push notification at 7 pm with tonight's personalised story ready to generate — one tap and it starts.
                  </p>
                </div>
                {son && (
                  <div className="form-check form-switch mb-0 ms-3">
                    <input className="form-check-input" type="checkbox" role="switch"
                      style={{cursor: son.pushAvailable ? 'pointer' : 'not-allowed', width:'48px', height:'24px'}}
                      checked={!!son.enabled}
                      disabled={!son.pushAvailable}
                      onChange={e => saveSon({ enabled: e.target.checked })} />
                  </div>
                )}
              </div>

              {!son ? (
                <div className="text-center py-3"><span className="spinner-border spinner-border-sm" style={{color:'#6f42c1'}}></span></div>
              ) : (
                <>
                  {!son.pushAvailable && (
                    <div className="alert alert-warning py-2 small mb-3">
                      <i className="bi bi-info-circle me-2"></i>
                      Enable push notifications in <strong>Bedtime Reminders</strong> above to use this feature.
                    </div>
                  )}

                  {sonMsg && (
                    <div className={`alert py-2 small mb-3 ${sonMsg.includes('ail') ? 'alert-danger' : 'alert-success'}`}>
                      {sonMsg}
                    </div>
                  )}

                  <div className={son.pushAvailable ? '' : 'opacity-50 pe-none'}>
                    <div className="mb-3">
                      <label className="form-label fw-semibold small">Notification Time</label>
                      <input type="time" className="form-control" style={{maxWidth:160}}
                        value={son.time || '19:00'}
                        onChange={e => saveSon({ time: e.target.value })} />
                      <div className="text-muted mt-1" style={{fontSize:'12px'}}>
                        <i className="bi bi-globe me-1"></i>Uses your reminder timezone
                        {reminder?.timezone && <span className="ms-1 fw-semibold">({(reminder.timezone || '').replace(/_/g,' ')})</span>}.
                      </div>
                    </div>

                    <div className="d-flex align-items-center gap-3 flex-wrap">
                      <button className="btn btn-outline-secondary rounded-pill"
                        onClick={handleSendTestSon} disabled={sonTestSending || sonSaving}>
                        {sonTestSending
                          ? <><span className="spinner-border spinner-border-sm me-2"></span>Sending...</>
                          : <><i className="bi bi-send me-2"></i>Send test notification</>}
                      </button>
                      {sonTestResult && (
                        <span className={`small ${sonTestResult.includes('ail') ? 'text-danger' : 'text-success'}`}>
                          {sonTestResult.includes('ail') ? '✗ ' : '✓ '}{sonTestResult}
                        </span>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Weekly Digest */}
          {flags.weeklyDigest !== false && <div className="card border-0 shadow-sm mb-4" style={{borderRadius:'16px'}}>
            <div className="card-body p-4">
              <div className="d-flex align-items-center justify-content-between mb-4">
                <h5 className="fw-bold mb-0" style={{color:'var(--text-main)'}}>
                  <i className="bi bi-envelope-open-fill me-2" style={{color:'#6f42c1'}}></i>Weekly Story Digest
                </h5>
                {digest && (
                  <div className="form-check form-switch mb-0">
                    <input className="form-check-input" type="checkbox" role="switch"
                      style={{cursor:'pointer', width:'48px', height:'24px'}}
                      checked={!!digest.enabled}
                      onChange={e => saveDigest({ enabled: e.target.checked })} />
                  </div>
                )}
              </div>

              {!digest ? (
                <div className="text-center py-3"><span className="spinner-border spinner-border-sm" style={{color:'#6f42c1'}}></span></div>
              ) : (
                <>
                  {digestMsg && (
                    <div className={`alert py-2 small mb-3 ${digestMsg.includes('ail') ? 'alert-danger' : 'alert-success'}`}>
                      {digestMsg}
                    </div>
                  )}

                  {/* What's in the digest */}
                  <div style={{background:'var(--purple-section)', borderRadius:12, padding:'12px 14px', marginBottom:16}}>
                    <div className="fw-semibold small mb-2" style={{color:'var(--text-main)'}}>What's in each newsletter:</div>
                    <div className="d-flex flex-column gap-1">
                      {[
                        ['📊','How many stories your child read this week'],
                        ['⭐','Their favourite story — title, life lesson & star rating'],
                        ['🔮','A personalised story idea for next week'],
                        ['✨','One-tap link to generate tonight\'s story'],
                      ].map(([icon, text]) => (
                        <div key={text} className="d-flex align-items-center gap-2 small" style={{color:'var(--text-body)'}}>
                          <span>{icon}</span><span>{text}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className={digest.enabled ? '' : 'opacity-50 pe-none'}>
                    <div className="mb-4">
                      <label className="form-label fw-semibold small">Send every</label>
                      <div className="d-flex flex-wrap gap-2">
                        {['monday','tuesday','wednesday','thursday','friday','saturday','sunday'].map(day => (
                          <button key={day} type="button"
                            className={`btn btn-sm rounded-pill ${(digest.day || 'sunday') === day ? 'btn-dream' : 'btn-outline-secondary'}`}
                            onClick={() => saveDigest({ day })}>
                            {day.charAt(0).toUpperCase() + day.slice(1)}
                          </button>
                        ))}
                      </div>
                      <p className="text-muted small mt-2 mb-0">
                        <i className="bi bi-clock me-1"></i>Sent at 9 AM in your reminder timezone.
                      </p>
                    </div>

                    <div className="d-flex align-items-center gap-3 flex-wrap">
                      <button className="btn btn-outline-secondary rounded-pill"
                        onClick={handleSendTestDigest} disabled={digestTestSending || digestSaving}>
                        {digestTestSending
                          ? <><span className="spinner-border spinner-border-sm me-2"></span>Sending...</>
                          : <><i className="bi bi-send me-2"></i>Send me a preview</>}
                      </button>
                      {digestTestResult && (
                        <span className={`small ${digestTestResult.includes('ail') ? 'text-danger' : 'text-success'}`}>
                          {digestTestResult.includes('ail') ? '✗ ' : '✓ '}{digestTestResult}
                        </span>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>}

          {/* Marketing Emails */}
          <div className="card border-0 shadow-sm mb-4" style={{borderRadius:'16px'}}>
            <div className="card-body p-4">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h5 className="fw-bold mb-0" style={{color:'var(--text-main)'}}>
                  <i className="bi bi-megaphone-fill me-2" style={{color:'#6f42c1'}}></i>Marketing Emails
                </h5>
                <div className="form-check form-switch mb-0">
                  <input className="form-check-input" type="checkbox" role="switch"
                    style={{cursor: marketingSaving ? 'not-allowed' : 'pointer', width:'48px', height:'24px'}}
                    checked={marketingOptIn}
                    disabled={marketingSaving}
                    onChange={e => handleMarketingToggle(e.target.checked)} />
                </div>
              </div>
              <p className="text-muted small mb-2">
                Receive occasional emails about new features, tips for creating better stories, and special offers. We never spam — typically 1–2 emails per month.
              </p>
              {marketingMsg && (
                <div className={`alert py-2 small mb-0 ${marketingMsg.includes('Failed') ? 'alert-danger' : 'alert-success'}`}>
                  {marketingMsg}
                </div>
              )}
            </div>
          </div>

          {/* My Voice */}
          {flags.voiceCloning !== false && <div className="card border-0 shadow-sm mb-4" style={{borderRadius:'16px'}}>
            <div className="card-body p-4">
              <div className="d-flex align-items-start justify-content-between gap-2 mb-1">
                <h5 className="fw-bold mb-0" style={{color:'var(--text-main)'}}>
                  <i className="bi bi-mic-fill me-2" style={{color:'#6f42c1'}}></i>My Voice
                  <span className="ms-2 badge rounded-pill text-white" style={{background:'#fd7e14', fontSize:'11px'}}>Family</span>
                </h5>
              </div>
              <p className="text-muted small mb-4">
                Record 30 seconds of your voice. Dreamy Bedtime clones it instantly using smart technology — every story your child hears will be narrated <strong>in your own voice</strong>, even when you're not there.
              </p>

              {isFamilyActive ? (
                <VoiceRecorder />
              ) : (
                <div className="p-3 rounded-3 d-flex align-items-start gap-3"
                  style={{background:'var(--orange-section)', border:'1px dashed var(--border-orange)'}}>
                  <i className="bi bi-lock-fill mt-1" style={{color:'#ea580c', fontSize:'18px'}}></i>
                  <div>
                    <div className="fw-semibold small mb-1" style={{color:'var(--text-orange)'}}>Family plan required</div>
                    <p className="text-muted small mb-2">
                      Voice cloning is available exclusively on the Family plan. Upgrade to let your children hear bedtime stories in your voice.
                    </p>
                    <Link to="/pricing" className="btn btn-sm rounded-pill px-3 fw-semibold"
                      style={{background:'#ea580c', color:'white', border:'none'}}>
                      Upgrade to Family
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>}

          {/* Blocked community authors.
              Blocking is required by Apple Guideline 1.2 / Google Play's UGC
              policy, and it shipped one-way: the API has an unblock endpoint but
              nothing in the app ever called it, so an accidental block was
              permanent. Deliberately a count and a single "unblock all" rather
              than a named list — the community is anonymous (stories expose only
              publishedChildName), so naming blocked authors here would reveal
              identities the product intentionally hides. */}
          {blockedCount > 0 && (
            <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '16px' }}>
              <div className="card-body p-4 d-flex flex-wrap align-items-center gap-3">
                <div className="flex-grow-1">
                  <h5 className="fw-bold mb-1" style={{ color: 'var(--text-main)' }}>
                    <i className="bi bi-slash-circle me-2"></i>Blocked storytellers
                  </h5>
                  <p className="mb-0 small" style={{ color: 'var(--text-muted2)' }}>
                    You've blocked {blockedCount} {blockedCount === 1 ? 'storyteller' : 'storytellers'}.
                    Their stories stay hidden from your community feed.
                  </p>
                </div>
                <button
                  className="btn btn-outline-secondary rounded-pill px-4"
                  disabled={unblocking}
                  onClick={handleUnblockAll}
                >
                  {unblocking
                    ? <><span className="spinner-border spinner-border-sm me-2"></span>Unblocking…</>
                    : 'Unblock all'}
                </button>
              </div>
            </div>
          )}

          {/* Referral */}
          <div id="referral" className="card border-0 shadow-sm mb-4" style={{ borderRadius: '16px', scrollMarginTop: '80px' }}>
            <div className="card-body p-4">
              <ReferralCard />
            </div>
          </div>

          {/* Subscription */}
          <div className="card border-0 shadow-sm" style={{borderRadius:'16px'}}>
            <div className="card-body p-4">
              <h5 className="fw-bold mb-4" style={{color:'var(--text-main)'}}>
                <i className="bi bi-credit-card-fill me-2"></i>Subscription
              </h5>
              <div className="d-flex align-items-start gap-3 mb-4 p-3 rounded-3" style={{background:'var(--header-bg)'}}>
                <div className="flex-grow-1">
                  <div className="fw-bold" style={{color:'var(--text-main)'}}>
                    {user?.subscription?.plan?.charAt(0).toUpperCase() + user?.subscription?.plan?.slice(1)} Plan
                    <span className={`ms-2 badge bg-${planColor === 'purple' ? 'secondary' : planColor}`}
                      style={planColor === 'purple' ? {background:'#6f42c1'} : {}}>
                      {user?.subscription?.status === 'trialing' ? 'Free trial' : (user?.subscription?.status || 'inactive')}
                    </span>
                    {user?.subscription?.billingInterval === 'year' && (
                      <span className="ms-2 badge rounded-pill"
                        style={{background:'#dcfce7', color:'#15803d', fontSize:'11px', fontWeight:700}}>
                        Annual
                      </span>
                    )}
                  </div>
                  {user?.subscription?.plan !== 'free' && (
                    user?.subscription?.status === 'trialing' ? (
                      <div className="text-muted small mt-1">
                        <i className="bi bi-stars me-1"></i>
                        Free trial
                        {(user?.subscription?.trialEnd || user?.subscription?.currentPeriodEnd) && (
                          <> · {user.subscription.cancelAtPeriodEnd ? 'Ends' : 'First charge'} {new Date(user.subscription.trialEnd || user.subscription.currentPeriodEnd).toLocaleDateString()}</>
                        )}
                      </div>
                    ) : (
                      <div className="text-muted small mt-1">
                        <i className="bi bi-calendar3 me-1"></i>
                        Billed {user?.subscription?.billingInterval === 'year' ? 'annually' : 'monthly'}
                        {user?.subscription?.currentPeriodEnd && (
                          <> · {user.subscription.cancelAtPeriodEnd ? 'Cancels' : 'Renews'} {new Date(user.subscription.currentPeriodEnd).toLocaleDateString()}</>
                        )}
                      </div>
                    )
                  )}
                  <div className="text-muted small mt-1">Stories this month: {user?.storiesGeneratedThisMonth || 0}</div>

                  {/* Upsell: suggest annual if on monthly paid plan */}
                  {user?.subscription?.plan !== 'free'
                    && ['active', 'trialing'].includes(user?.subscription?.status)
                    && user?.subscription?.billingInterval !== 'year' && (
                    <div className="mt-2 p-2 rounded-2 d-flex align-items-center gap-2"
                      style={{background:'#f0fdf4', border:'1px solid #86efac'}}>
                      <i className="bi bi-piggy-bank-fill" style={{color:'#16a34a'}}></i>
                      <span style={{fontSize:'12px', color:'#14532d'}}>
                        Switch to annual and <strong>save 2 months</strong> every year.
                        <Link to="/pricing" className="ms-1 fw-semibold" style={{color:'#16a34a'}}>See annual prices →</Link>
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div className="d-flex flex-wrap gap-2">
                {user?.subscription?.plan !== 'free' && (
                  <button className="btn btn-outline-secondary rounded-pill" onClick={handlePortal}>
                    <i className="bi bi-arrow-up-right me-2"></i>Manage Billing
                  </button>
                )}
                {shouldUseIap() && (
                  <button className="btn btn-outline-secondary rounded-pill" onClick={handleRestore}>
                    <i className="bi bi-arrow-clockwise me-2"></i>Restore Purchases
                  </button>
                )}
                <Link to="/pricing" className="btn btn-dream rounded-pill">
                  <i className="bi bi-star me-2"></i>
                  {user?.subscription?.plan === 'free' ? 'Upgrade Plan' : 'Change Plan'}
                </Link>
              </div>
            </div>
          </div>

          {/* Family Members — Family plan only, shown only to primary parent */}
          {isFamilyActive && !user?.coParentOf && (
            <div className="card border-0 shadow-sm mb-4" style={{borderRadius:'16px'}}>
              <div className="card-body p-4">
                <h5 className="fw-bold mb-1" style={{color:'var(--text-main)'}}>
                  <i className="bi bi-people-fill me-2" style={{color:'#6f42c1'}}></i>Family Members
                </h5>
                <p className="text-muted small mb-4">Invite a co-parent or grandparent to access the same children and library.</p>

                {familyMsg.text && (
                  <div className={`alert py-2 small mb-3 ${familyMsg.type === 'success' ? 'alert-success' : 'alert-danger'}`}>
                    {familyMsg.text}
                  </div>
                )}

                {/* Invite form */}
                <form onSubmit={handleSendInvite} className="d-flex flex-wrap gap-2 align-items-end mb-4">
                  <div className="flex-grow-1" style={{minWidth:'180px'}}>
                    <label className="form-label small fw-semibold mb-1">Email address</label>
                    <input
                      type="email" className="form-control" placeholder="co-parent@example.com"
                      value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} required
                    />
                  </div>
                  <div style={{minWidth:'160px'}}>
                    <label className="form-label small fw-semibold mb-1">Access level</label>
                    <select className="form-select" value={inviteRole} onChange={e => setInviteRole(e.target.value)}>
                      <option value="read_only">Read only</option>
                      <option value="can_generate">Can generate stories</option>
                    </select>
                  </div>
                  <button type="submit" className="btn btn-dream rounded-pill px-4" disabled={inviteSending}
                    style={{whiteSpace:'nowrap'}}>
                    {inviteSending
                      ? <><span className="spinner-border spinner-border-sm me-2"></span>Sending…</>
                      : <><i className="bi bi-envelope-plus me-2"></i>Send Invite</>}
                  </button>
                </form>

                {familyLoading ? (
                  <div className="text-center py-3"><span className="spinner-border spinner-border-sm" style={{color:'#6f42c1'}}></span></div>
                ) : (
                  <>
                    {/* Active members */}
                    {familyData?.members?.length > 0 && (
                      <div className="mb-3">
                        <p className="small fw-semibold text-muted mb-2">Active members</p>
                        {familyData.members.map(m => (
                          <div key={m._id} className="d-flex align-items-center gap-3 p-3 rounded-3 mb-2"
                            style={{background:'var(--header-bg)'}}>
                            <div className="fw-semibold flex-grow-1" style={{color:'var(--text-main)'}}>
                              <div>{m.name}</div>
                              <div className="text-muted small">{m.email}</div>
                            </div>
                            <select className="form-select form-select-sm" style={{width:'auto'}}
                              value={m.coParentRole}
                              onChange={e => handleToggleRole(m._id, e.target.value)}>
                              <option value="read_only">Read only</option>
                              <option value="can_generate">Can generate</option>
                            </select>
                            <button className="btn btn-sm btn-outline-danger rounded-pill"
                              onClick={() => handleRemoveMember(m._id)}>
                              <i className="bi bi-person-x"></i>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Pending invites */}
                    {familyData?.pendingInvites?.length > 0 && (
                      <div>
                        <p className="small fw-semibold text-muted mb-2">Pending invites</p>
                        {familyData.pendingInvites.map(inv => (
                          <div key={inv._id} className="d-flex align-items-center gap-3 p-3 rounded-3 mb-2"
                            style={{background:'var(--header-bg)'}}>
                            <div className="flex-grow-1" style={{color:'var(--text-main)'}}>
                              <div>{inv.inviteeEmail}</div>
                              <div className="text-muted small">
                                {inv.role === 'can_generate' ? 'Can generate' : 'Read only'} ·
                                Expires {new Date(inv.expiresAt).toLocaleDateString()}
                              </div>
                            </div>
                            <span className="badge bg-warning text-dark">Pending</span>
                            <button className="btn btn-sm btn-outline-secondary rounded-pill"
                              onClick={() => handleRevokeInvite(inv._id)}>
                              Revoke
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {!familyData?.members?.length && !familyData?.pendingInvites?.length && (
                      <p className="text-muted small text-center py-2">No family members yet. Send an invite above.</p>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {/* Co-parent card — shown to co-parents */}
          {user?.coParentOf && (
            <div className="card border-0 shadow-sm mb-4" style={{borderRadius:'16px', overflow:'hidden'}}>
              {/* Header stripe */}
              <div style={{background:'linear-gradient(135deg,#6f42c1,#9b59b6)', padding:'20px 24px'}}>
                <div className="d-flex align-items-center gap-3">
                  <div className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                    style={{width:'44px', height:'44px', background:'rgba(255,255,255,0.15)', fontSize:'20px'}}>
                    👨‍👩‍👧
                  </div>
                  <div>
                    <div style={{color:'#fff', fontWeight:700, fontSize:'16px'}}>Family Member Access</div>
                    <div style={{color:'rgba(255,255,255,0.75)', fontSize:'13px'}}>
                      {user.coParentRole === 'can_generate'
                        ? 'You can view and generate stories'
                        : 'You can view stories (read only)'}
                    </div>
                  </div>
                  <span className="ms-auto badge rounded-pill"
                    style={{
                      background: user.coParentRole === 'can_generate' ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.15)',
                      color: '#fff', fontSize: '11px', padding: '5px 10px',
                    }}>
                    {user.coParentRole === 'can_generate' ? '✏️ Can Generate' : '📖 Read Only'}
                  </span>
                </div>
              </div>

              {/* Body */}
              <div className="p-4">
                {familyMsg.text && (
                  <div className={`alert py-2 small mb-3 ${familyMsg.type === 'success' ? 'alert-success' : 'alert-danger'}`}>
                    {familyMsg.text}
                  </div>
                )}

                {/* Primary parent info */}
                {familyData?.primaryParent ? (
                  <div className="d-flex align-items-center gap-3 p-3 rounded-3 mb-4"
                    style={{background:'var(--header-bg)', border:'1px solid var(--border-purple)'}}>
                    <div className="d-flex align-items-center justify-content-center rounded-circle fw-bold text-white flex-shrink-0"
                      style={{width:'40px', height:'40px', background:'#6f42c1', fontSize:'15px'}}>
                      {familyData.primaryParent.name?.charAt(0).toUpperCase()}
                    </div>
                    <div style={{flex:1}}>
                      <div style={{fontWeight:600, fontSize:'14px', color:'var(--text-main)'}}>
                        {familyData.primaryParent.name}
                      </div>
                      <div style={{fontSize:'12px', color:'var(--text-muted2)'}}>
                        {familyData.primaryParent.email} · Family plan owner
                      </div>
                    </div>
                  </div>
                ) : familyLoading ? (
                  <div className="text-center py-2 mb-4">
                    <span className="spinner-border spinner-border-sm" style={{color:'#6f42c1'}}></span>
                  </div>
                ) : null}

                {/* What you can do */}
                <div className="mb-4">
                  <p className="small fw-semibold mb-2" style={{color:'var(--text-muted2)'}}>Your access includes</p>
                  <div className="d-flex flex-column gap-2">
                    {[
                      { icon: 'bi-people', text: "View the family's child profiles" },
                      { icon: 'bi-book', text: "Browse and read the story library" },
                      ...(user.coParentRole === 'can_generate'
                        ? [{ icon: 'bi-stars', text: "Generate new stories (uses family's monthly quota)" }]
                        : []),
                    ].map(item => (
                      <div key={item.text} className="d-flex align-items-center gap-2">
                        <i className={`bi ${item.icon}`} style={{color:'#6f42c1', fontSize:'14px', width:'16px'}}></i>
                        <span style={{fontSize:'13px', color:'var(--text-body)'}}>{item.text}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Leave button */}
                <button
                  className="btn btn-outline-danger rounded-pill w-100"
                  onClick={handleLeaveFamily}
                  disabled={leavingFamily}
                  style={{fontSize:'14px'}}>
                  {leavingFamily
                    ? <><span className="spinner-border spinner-border-sm me-2"></span>Leaving…</>
                    : <><i className="bi bi-box-arrow-left me-2"></i>Leave this Family</>}
                </button>
                <p className="text-muted small text-center mt-2 mb-0">
                  Your own data will reappear on your account after leaving.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="col-lg-4">
          <div className="card border-0 shadow-sm" style={{borderRadius:'16px'}}>
            <div className="card-body p-4">
              <h6 className="fw-bold mb-3 text-danger">
                <i className="bi bi-exclamation-triangle me-2"></i>Account
              </h6>
              <p className="text-muted small mb-3">Sign out from this device or delete your account.</p>
              <div className="d-grid gap-2">
                <button className="btn btn-outline-secondary rounded-pill" onClick={() => { trackLogout(); logout(); }}>
                  <i className="bi bi-box-arrow-right me-2"></i>Sign Out
                </button>
                <button className="btn btn-outline-warning rounded-pill small" onClick={handleSignOutOtherDevices} disabled={signOutLoading}>
                  {signOutLoading
                    ? <><span className="spinner-border spinner-border-sm me-2"></span>Signing out…</>
                    : <><i className="bi bi-shield-lock me-2"></i>Sign Out Other Devices</>}
                </button>
                <button className="btn btn-outline-danger rounded-pill small"
                  onClick={handleDeleteAccount} disabled={deletingAccount}>
                  {deletingAccount
                    ? <><span className="spinner-border spinner-border-sm me-2"></span>Deleting…</>
                    : <><i className="bi bi-trash me-2"></i>Delete Account</>}
                </button>
              </div>
              {signOutMsg && (
                <p className="small mt-2 mb-0" style={{color: signOutMsg.startsWith('Failed') ? '#dc3545' : '#198754'}}>{signOutMsg}</p>
              )}
            </div>
          </div>

          <div className="card border-0 shadow-sm mt-3 p-4" style={{borderRadius:'16px', background: dm ? 'rgba(111,66,193,0.2)' : 'linear-gradient(135deg,#f0e8ff,#e8d5ff)'}}>
            <div className="fs-2 mb-2">🌙</div>
            <p className="fw-bold mb-1 small" style={{color:'var(--text-main)'}}>Need help?</p>
            <p className="text-muted small mb-2">Our team is happy to help with any questions.</p>
            <a href="mailto:support@dreamybedtime.com" className="btn btn-sm btn-dream rounded-pill px-3">Contact Support</a>
          </div>

          <div className="card border-0 shadow-sm mt-3 p-4" style={{borderRadius:'16px'}}>
            <div className="fs-2 mb-2">🗺️</div>
            <p className="fw-bold mb-1 small" style={{color:'var(--text-main)'}}>Take the tour again</p>
            <p className="text-muted small mb-3">Replay the quick walkthrough to rediscover features.</p>
            <button
              className="btn btn-sm rounded-pill px-4 fw-semibold"
              style={{background:'linear-gradient(135deg,#6f42c1,#9b59b6)', color:'#fff', border:'none'}}
              onClick={replayTour}
            >
              <i className="bi bi-play-circle me-2"></i>Start Tour
            </button>
          </div>
        </div>
      </div>

    </DashboardLayout>
  );
};

export default SettingsPage;
